import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { CustomizedTreeView } from "./components/CustomizedComponents";
import RecursiveTreeView from "./components/RecursiveTreeView";
import { useSelector, useDispatch } from "react-redux";
import RightClickMenu from "./components/RightClickMenu";
import PropTypes from "prop-types";
import { controlClicked } from "../../SharedActions/deviceProfileActions";
import { canManageByApplication } from "orion-components/Profiles/Selectors";
import { generateDirStyles } from "../../SharedComponents/GenerateDirStyles/GenerateDirStyles";
import { getDir } from "orion-components/i18n/Config/selector";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";
import { openDialog } from "orion-components/AppState/Actions";
import { compositeDeviceService } from "client-app-core";
import { deviceGroupsService } from "client-app-core";
import { deleteDeviceGroup } from "../../GlobalData/DeviceGroups/actions";
import { loadProfile } from "orion-components/ContextPanel/Actions";
import { getTranslation } from "orion-components/i18n/Actions/index.js";
import EventBus from "orion-components/SharedComponents/EventBus";

const baseStyles = {
	container: { marginLeft: "20px", marginRight: "20px" },
	treeSx: { flexGrow: 1, overflowY: "auto", color: "#FFFFFF", overflowX: "hidden" }
};

const CompositeDeviceTree = ({
	addToDeviceGroup,
	getSelectedNodes,
	readOnly,
	isForFacilitiesFloorPlan,
	getSelectedDevice,
	externalTreeData
}) => {
	const [contextMenu, setContextMenu] = useState(null);
	const [selectedNode, setSelectedNode] = useState(null);
	const [rightClickFor, setRightClickFor] = useState("group");
	const [basicDeviceCommands, setBasicDeviceCommands] = useState([]);
	const [expandedNodes, setExpandedNodes] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);

	const dir = useSelector(state => getDir(state));
	const styles = generateDirStyles(baseStyles, dir);
	const canManageCamerasApp = useSelector(state => canManageByApplication(state, "cameras-app", "manage"));
	const deviceGroups = externalTreeData || [];

	const dispatch = useDispatch();

	// Function to extract all node IDs (including children) for auto-expansion
	const getAllNodeIds = nodes => {
		const ids = [];
		const traverse = node => {
			ids.push(node.id);
			if (node.children && node.children.length > 0) {
				node.children.forEach(traverse);
			}
		};
		nodes.forEach(traverse);
		return ids;
	};

	// Automatically expand all nodes when deviceGroups change
	useEffect(() => {
		if (deviceGroups.length > 0) {
			const expandedIds = getAllNodeIds(deviceGroups);
			setExpandedNodes(expandedIds);
		}
	}, [deviceGroups]);

	useEffect(() => {
		const handleActionIconClick = data => {
			handleRightClick(data?.event, data?.node, true);
		};

		EventBus.subscribe("accessControl_actionIconClick", handleActionIconClick);

		return () => {
			EventBus.unsubscribe("accessControl_actionIconClick", handleActionIconClick);
		};
	}, []);

	const handleRightClick = (event, node, actionIconClick) => {
		if (readOnly) return;
		event.preventDefault();

		// Adjust the position to ensure the menu does not overflow the viewport
		const menuWidth = 210; // Approximate width of your menu
		const menuHeight = 210; // Approximate height of your menu

		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		const adjustedX =
			event.clientX + menuWidth > viewportWidth
				? viewportWidth - menuWidth - 10 // Ensure menu stays within viewport
				: event.clientX - 20;

		const adjustedY =
			event.clientY + menuHeight > viewportHeight
				? viewportHeight - menuHeight - 10 // Ensure menu stays within viewport
				: event.clientY - 55;

		setContextMenu({
			mouseX: adjustedX,
			mouseY: adjustedY
		});

		const clickFor = actionIconClick
			? "compositeDevice_actionIconClick"
			: node?.entityType === "compositeDevice"
				? "compositeDevice"
				: "basicDevice";

		setRightClickFor(clickFor);
		const features = node?.entityData?.properties?.features;
		const commands = node?.entityData?.properties?.commands || [];
		const availableCommands = features?.length ? features : commands;
		const deviceCommandsMenuItem =
			availableCommands.map((command, index) => ({
				id: `command_menu_${index}`,
				canAccessMenu: true,
				item: command?.label || command?.command,
				action: () => {
					dispatch(controlClicked(command, node.id));
				},
				hasNestedMenu: false
			})) || [];

		setBasicDeviceCommands(deviceCommandsMenuItem);
		setSelectedNode(node);
	};

	const getSelectedMenuItem = (menuItem, node) => {
		const targetNode = node || selectedNode;
		switch (menuItem) {
			case "Delete":
				deviceGroupsService.deleteDeviceGroup(selectedNode.id, err => {
					if (err) {
						console.log(err, "unable to delete device group");
					} else {
						dispatch(deleteDeviceGroup(selectedNode.id));
					}
				});
				break;

			case "addToGroup":
				{
					setSelectedRows([{ type: selectedNode?.entityType, id: selectedNode?.id }]);
					dispatch(openDialog("addToDeviceGroup-dialog"));
				}
				break;

			case "unmap":
				{
					setSelectedRows([{ type: selectedNode?.entityType, id: selectedNode?.id }]);
					dispatch(openDialog("unmap-dialog"));
				}
				break;

			case "openItem":
				{
					dispatch(loadProfile(targetNode?.id, targetNode?.name, targetNode?.entityType, "profile"));
				}

				break;

			case "assignDeviceType":
				dispatch(openDialog("assignDeviceType-dialog"));
				break;
			case "addToCompositeDevice":
				setSelectedNode([{ type: selectedNode?.entityType, id: selectedNode?.id }]);
				dispatch(openDialog("addToCompositeDevice-dialog"));
				break;
			case "removeFromCompositeDevice":
				{
					const devices = [{ type: selectedNode?.entityType, id: selectedNode?.id }];

					const body = { devices };
					compositeDeviceService.removeCompositeDevices(selectedNode?.compositeDeviceId, body, err => {
						if (err) {
							const errorMessage = {
								message: getTranslation(
									"global.deviceManager.deviceTree.removalFailedFromCompositeDevice",
									{ primaryValue: selectedNode?.name }
								),
								error: err
							};
							console.error("Error Details:", errorMessage);
							dispatch(
								createUserFeedback(
									getTranslation(
										"global.deviceManager.deviceTree.failedRemovingFromCompositeDevice",
										{ primaryValue: selectedNode?.name }
									),
									null,
									true
								)
							);
							return;
						} else {
							dispatch(
								createUserFeedback(
									getTranslation(
										"global.deviceManager.deviceTree.removedSuccessfullyFromCompositeDevice",
										{ primaryValue: selectedNode?.name }
									),
									null,
									false
								)
							);
						}
					});
				}
				break;
			case "removeFromGroup":
				{
					const devices = [{ type: selectedNode?.entityType, id: selectedNode?.id }];
					const body = { devices };
					deviceGroupsService.removeDevices(selectedNode?.parentGroupId, body, err => {
						if (err) {
							const errorMessage = {
								message: getTranslation(
									"global.deviceManager.deviceTree.failedRemovingFromDeviceGroup",
									{ primaryValue: selectedNode?.name }
								),
								error: err
							};
							console.error("Error Details:", errorMessage);
							dispatch(
								createUserFeedback(
									getTranslation("global.deviceManager.deviceTree.errorRemovingFromDeviceGroup", {
										name: selectedNode?.name
									}),
									null,
									true
								)
							);
							return;
						}

						dispatch(
							createUserFeedback(
								getTranslation("global.deviceManager.deviceTree.deviceRemovedFromGroupSuccess", {
									name: selectedNode?.name
								}),
								null,
								false
							)
						);
					});
				}
				break;
			default:
				return;
		}
		setContextMenu(null);
	};

	return (
		<Box sx={{ ...(!readOnly && { ...styles.container }) }}>
			<CustomizedTreeView expanded={expandedNodes} sx={styles.treeSx}>
				{deviceGroups.length > 0 &&
					deviceGroups.map(node => (
						<Box
							key={node?.id}
							sx={{
								minWidth: 300,
								overflowX: "auto",
								"&::-webkit-scrollbar": {
									height: "13px"
								}
							}}
						>
							<RecursiveTreeView
								key={node?.id}
								nodes={[node]}
								expandedNodes={expandedNodes}
								onContextMenu={handleRightClick}
								addToDeviceGroup={addToDeviceGroup}
								readOnly={readOnly}
								getSelectedNodes={getSelectedNodes}
								isForFacilitiesFloorPlan={isForFacilitiesFloorPlan}
								getSelectedDevice={getSelectedDevice}
								displayChevrons={false}
								getSelectedMenuItem={getSelectedMenuItem} data-testid="orion-components-device-manager-device-tree-composite-device-tree-recursive-tree-view"
							/>
						</Box>
					))}
			</CustomizedTreeView>

			{/* Right-click menu */}
			{contextMenu && canManageCamerasApp && !isForFacilitiesFloorPlan && (
				<RightClickMenu
					contextMenu={contextMenu}
					onClose={() => setContextMenu(null)}
					node={selectedNode}
					menuFor={rightClickFor}
					basicDeviceCommands={basicDeviceCommands}
					getSelectedMenuItem={getSelectedMenuItem}
					selectedRows={selectedRows} data-testid="orion-components-device-manager-device-tree-composite-device-tree-right-click-menu"
				/>
			)}
		</Box>
	);
};

CompositeDeviceTree.propTypes = {
	addToDeviceGroup: PropTypes.func.isRequired,
	getSelectedNodes: PropTypes.func,
	readOnly: PropTypes.bool,
	isForFacilitiesFloorPlan: PropTypes.bool,
	getSelectedDevice: PropTypes.func,
	externalTreeData: PropTypes.array
};

CompositeDeviceTree.defaultProps = {
	getSelectedNodes: () => {},
	readOnly: false,
	isForFacilitiesFloorPlan: false,
	getSelectedDevice: () => {},
	externalTreeData: []
};

export default CompositeDeviceTree;
